import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Mail, Phone, ExternalLink, Link2 } from "lucide-react";
import { get, patch, post } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "converted", "lost"];
const STATUS_COLOR = {
    new: "text-[#00B7FF] border-[#1E90FF]/40 bg-[#0a1429]",
    contacted: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#181301]",
    qualified: "text-[#a3e635] border-[#a3e635]/40 bg-[#0c1505]",
    converted: "text-[#22d3ee] border-[#22d3ee]/40 bg-[#05151a]",
    lost: "text-[#a3b5d1] border-[#1a2a4a] bg-[#0a1018]",
};

export default function Prospects() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [convertOpen, setConvertOpen] = useState(false);
    const [convertData, setConvertData] = useState({
        plan_price_usd: "3000",
        payment_terms: "paid_in_full",
        plan: "90 Day Alpha",
        start_date: new Date().toISOString().slice(0, 10),
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await get("/admin/prospects");
            setRows(data);
        } catch {
            toast.error("Failed to load prospects.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = async (p, status) => {
        setSavingId(p.id);
        try {
            const next = await patch(`/admin/prospects/${p.id}`, { status });
            setRows((r) => r.map((x) => (x.id === p.id ? next : x)));
            if (active?.id === p.id) setActive(next);
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Update failed");
        } finally {
            setSavingId(null);
        }
    };

    const saveNotes = async (p, notes) => {
        setSavingId(p.id);
        try {
            const next = await patch(`/admin/prospects/${p.id}`, { notes });
            setRows((r) => r.map((x) => (x.id === p.id ? next : x)));
            setActive(next);
            toast.success("Notes saved");
        } catch {
            toast.error("Save failed");
        } finally {
            setSavingId(null);
        }
    };

    const sendApplication = async (p) => {
        setSavingId(p.id);
        try {
            const res = await post("/admin/applications/invite", {
                consultation_id: p.id,
            });
            const fullUrl = res.url.startsWith("http")
                ? res.url
                : `${window.location.origin}/apply/${res.token}`;
            try {
                await navigator.clipboard.writeText(fullUrl);
                toast.success("Application link copied to clipboard");
            } catch {
                toast.success(`Application link: ${fullUrl}`);
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not create application link"
            );
        } finally {
            setSavingId(null);
        }
    };

    const convert = async () => {
        if (!active) return;
        const price = parseFloat(convertData.plan_price_usd);
        if (!price || price <= 0) {
            toast.error("Enter a valid plan price");
            return;
        }
        try {
            setSavingId(active.id);
            await post("/admin/clients", {
                consultation_id: active.id,
                name: active.name,
                email: active.email,
                phone: active.phone,
                plan: convertData.plan,
                plan_price_usd: price,
                payment_terms: convertData.payment_terms,
                start_date: convertData.start_date,
                status: "active",
                notes: active.notes || "",
            });
            toast.success(`${active.name} → client created`);
            setConvertOpen(false);
            setActive(null);
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not convert prospect"
            );
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-prospects">
            <header className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                    Pipeline
                </p>
                <h1 className="mt-2 font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                    Prospects
                </h1>
                <p className="mt-2 text-white/55 text-sm">
                    Consultations booked through the site. Update status, take
                    notes, and convert qualified leads into clients.
                </p>
            </header>

            <div className="bg-[#060c1e] border border-[#1a2a4a] overflow-hidden">
                <div className="grid grid-cols-12 px-5 py-3 border-b border-[#1a2a4a] text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3 hidden md:block">Contact</div>
                    <div className="col-span-2">Scheduled</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 hidden md:block">Booked</div>
                </div>

                {loading && (
                    <div className="px-5 py-12 text-white/55 text-sm flex items-center gap-3">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="px-5 py-12 text-white/55 text-sm">
                        No prospects yet. Bookings made on /book will show here.
                    </div>
                )}

                {!loading &&
                    rows.map((p) => {
                        const isActive = active?.id === p.id;
                        const color = STATUS_COLOR[p.status] || STATUS_COLOR.new;
                        return (
                            <div
                                key={p.id}
                                data-testid={`prospect-row-${p.id}`}
                                className={`border-b border-[#142142] last:border-b-0 transition-colors ${
                                    isActive ? "bg-[#0a1429]" : "hover:bg-[#0a1429]/40"
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() =>
                                        setActive(isActive ? null : p)
                                    }
                                    className="w-full text-left grid grid-cols-12 px-5 py-4 items-center"
                                >
                                    <div className="col-span-3">
                                        <p className="text-white font-semibold text-sm">
                                            {p.name}
                                        </p>
                                        <p className="text-white/50 text-[11px]">
                                            {p.current_situation}
                                        </p>
                                    </div>
                                    <div className="col-span-3 hidden md:block">
                                        <p className="text-white/85 text-xs flex items-center gap-1.5">
                                            <Mail size={11} />
                                            {p.email}
                                        </p>
                                        <p className="text-white/55 text-[11px] flex items-center gap-1.5 mt-1">
                                            <Phone size={11} />
                                            {p.phone}
                                        </p>
                                    </div>
                                    <div className="col-span-2 text-white/85 text-xs">
                                        <p className="font-semibold">
                                            {p.scheduled_date}
                                        </p>
                                        <p className="text-white/50">
                                            {p.scheduled_time}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <span
                                            className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border ${color}`}
                                        >
                                            {p.status || "new"}
                                        </span>
                                    </div>
                                    <div className="col-span-2 hidden md:block text-white/50 text-[11px]">
                                        {new Date(p.created_at).toLocaleDateString()}
                                    </div>
                                </button>

                                {isActive && (
                                    <div className="px-5 pb-6 -mt-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
                                            <div className="bg-[#020617] border border-[#1a2a4a] p-4">
                                                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                                                    Biggest Challenge
                                                </p>
                                                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {p.biggest_challenge}
                                                </p>
                                                <p className="mt-4 text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                                                    Meta
                                                </p>
                                                <ul className="text-white/65 text-xs space-y-1">
                                                    <li>
                                                        Age: {p.age_range}
                                                    </li>
                                                    <li>
                                                        Timezone:{" "}
                                                        {p.timezone_name}
                                                    </li>
                                                    <li>
                                                        Committed:{" "}
                                                        {p.commitment
                                                            ? "Yes"
                                                            : "No"}
                                                    </li>
                                                </ul>
                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                                                    Status
                                                </p>
                                                <div className="flex flex-wrap gap-2 mb-4">
                                                    {STATUSES.map((s) => (
                                                        <button
                                                            key={s}
                                                            type="button"
                                                            data-testid={`set-status-${s}-${p.id}`}
                                                            disabled={
                                                                savingId === p.id
                                                            }
                                                            onClick={() =>
                                                                updateStatus(p, s)
                                                            }
                                                            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-bold border transition-all ${
                                                                p.status === s
                                                                    ? "bg-[#1E90FF] text-white border-[#1E90FF]"
                                                                    : "bg-transparent text-white/65 border-[#1a2a4a] hover:border-[#1E90FF] hover:text-white"
                                                            }`}
                                                        >
                                                            {s}
                                                        </button>
                                                    ))}
                                                </div>
                                                <NotesEditor
                                                    initial={p.notes || ""}
                                                    saving={savingId === p.id}
                                                    onSave={(notes) =>
                                                        saveNotes(p, notes)
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    data-testid={`convert-button-${p.id}`}
                                                    onClick={() =>
                                                        setConvertOpen(true)
                                                    }
                                                    className="mt-4 inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#C5A059] transition-all shadow-[0_0_30px_rgba(212,175,55,0.25)]"
                                                >
                                                    Convert to Client{" "}
                                                    <ExternalLink size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid={`send-application-${p.id}`}
                                                    onClick={() =>
                                                        sendApplication(p)
                                                    }
                                                    disabled={savingId === p.id}
                                                    className="mt-3 ml-0 sm:ml-2 inline-flex items-center gap-2 px-5 py-3 bg-transparent border border-[#1a2a4a] text-white font-bold uppercase tracking-[0.18em] text-xs hover:border-[#1E90FF] hover:text-[#00B7FF] transition-all disabled:opacity-50"
                                                >
                                                    {savingId === p.id ? (
                                                        <Loader2
                                                            className="animate-spin"
                                                            size={14}
                                                        />
                                                    ) : (
                                                        <Link2 size={14} />
                                                    )}
                                                    Send Application Link
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            {convertOpen && active && (
                <div
                    data-testid="convert-modal"
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-5"
                    onClick={() => setConvertOpen(false)}
                >
                    <div
                        className="bg-[#060c1e] border border-[#1a2a4a] max-w-md w-full p-7"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00B7FF]">
                            Convert Prospect
                        </p>
                        <h3 className="mt-2 font-display font-black text-white text-2xl tracking-tight">
                            {active.name} → Client
                        </h3>
                        <div className="mt-5 space-y-4">
                            <Field
                                label="Plan"
                                value={convertData.plan}
                                onChange={(v) =>
                                    setConvertData((d) => ({ ...d, plan: v }))
                                }
                                testId="convert-plan"
                            />
                            <Field
                                label="Plan price (USD)"
                                type="number"
                                value={convertData.plan_price_usd}
                                onChange={(v) =>
                                    setConvertData((d) => ({
                                        ...d,
                                        plan_price_usd: v,
                                    }))
                                }
                                testId="convert-price"
                            />
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-white/65 mb-1.5">
                                    Payment terms
                                </label>
                                <select
                                    data-testid="convert-terms"
                                    value={convertData.payment_terms}
                                    onChange={(e) =>
                                        setConvertData((d) => ({
                                            ...d,
                                            payment_terms: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white px-3 py-2.5 text-sm"
                                >
                                    <option value="paid_in_full">
                                        Paid in full
                                    </option>
                                    <option value="installments">
                                        Installments
                                    </option>
                                </select>
                            </div>
                            <Field
                                label="Start date"
                                type="date"
                                value={convertData.start_date}
                                onChange={(v) =>
                                    setConvertData((d) => ({
                                        ...d,
                                        start_date: v,
                                    }))
                                }
                                testId="convert-start"
                            />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                data-testid="convert-confirm"
                                disabled={savingId === active.id}
                                onClick={convert}
                                className="inline-flex items-center justify-center gap-2 flex-1 bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-[#C5A059] transition-all disabled:opacity-50"
                            >
                                {savingId === active.id ? (
                                    <Loader2 className="animate-spin" size={14} />
                                ) : (
                                    <CheckCircle2 size={14} />
                                )}
                                Confirm
                            </button>
                            <button
                                type="button"
                                onClick={() => setConvertOpen(false)}
                                className="px-5 py-3 text-white/65 hover:text-white text-xs uppercase tracking-[0.18em] font-bold"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function NotesEditor({ initial, onSave, saving }) {
    const [val, setVal] = useState(initial || "");
    useEffect(() => setVal(initial || ""), [initial]);
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                Notes
            </p>
            <textarea
                data-testid="prospect-notes"
                rows={4}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="Internal notes (only you see this)…"
                className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white text-sm px-3 py-2.5 resize-none"
            />
            <button
                type="button"
                data-testid="prospect-notes-save"
                disabled={saving || val === (initial || "")}
                onClick={() => onSave(val)}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#1a2a4a] text-white text-[11px] uppercase tracking-[0.15em] font-bold hover:border-[#1E90FF] hover:text-[#00B7FF] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                {saving ? (
                    <Loader2 className="animate-spin" size={12} />
                ) : null}
                Save Notes
            </button>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testId }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-white/65 mb-1.5">
                {label}
            </label>
            <input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white px-3 py-2.5 text-sm"
            />
        </div>
    );
}
